import * as React from 'react'
import Client from '../../../client'
import { LoadableSelect } from '../../../components/Inputs'

interface Props {
  onChange: (tags?: string[]) => void
  value?: string[]
  placeholder?: string
  createable?: boolean
  allowUntaggedFilter?: boolean
  disabled?: boolean
  limitResults?: number
}

type TagFormat = { value: string; label: string }[]

// Constants for the special "(Untagged)" tag option
const UNTAGGED_VALUE = '-1'
const UNTAGGED_LABEL = '(Untagged)'
const style = { margin: 0, textAlign: 'left' }

const TagSelector: React.FC<Props> = React.memo(
  ({
    onChange,
    value,
    placeholder = 'Select or create tags',
    createable = true,
    allowUntaggedFilter = false,
    disabled = false,
    limitResults
  }) => {
    const [isInitialized, setIsInitialized] = React.useState(false)

    React.useEffect(() => {
      // Mark as initialized after first render to prevent unwanted onChange calls
      setIsInitialized(true)
    }, [])

    const onChangeInner = React.useCallback(
      v => {
        // Only process onChange if the component is fully initialized
        if (!isInitialized) {
          return
        }

        // Convert (Untagged) back to -1 for internal representation
        const newValues = v.map(i => (i.value === UNTAGGED_LABEL ? UNTAGGED_VALUE : i.value)).filter(val => val !== '')
        // Only call onChange if the values actually changed
        const currentValues = value || []
        if (JSON.stringify(newValues.sort()) !== JSON.stringify(currentValues.sort())) {
          onChange(newValues)
        }
      },
      [onChange, isInitialized, value]
    )

    // Convert -1 back to (Untagged) for display in the dropdown
    const displayValue = React.useMemo(() => {
      if (!value) return value
      return value.map(v => (v === UNTAGGED_VALUE ? UNTAGGED_LABEL : v))
    }, [value])

    const customLoad = React.useCallback(
      (searchString?: string) =>
        Client.productTags(limitResults, searchString).then(response => {
          return response.data
        }),
      [limitResults]
    )

    const process = React.useCallback(
      (d: any): TagFormat => {
        const options: TagFormat = d.map(t => ({ value: t.title, label: t.title }))

        // If a template builder injects tags, we need to first add it to the option list
        if (value) {
          value.filter(Boolean).forEach(v => {
            const displayVal = v === UNTAGGED_VALUE ? UNTAGGED_LABEL : v
            if (options.findIndex(opt => opt.value === displayVal) === -1) {
              options.push({ value: displayVal, label: displayVal })
            }
          })
        }

        // Always add (Untagged) option at the end so it's available but not at the start of the list
        if (allowUntaggedFilter && !options.find(opt => opt.value === UNTAGGED_LABEL)) {
          options.push({ value: UNTAGGED_LABEL, label: UNTAGGED_LABEL })
        }

        return options
      },
      [allowUntaggedFilter, value]
    )
    return (
      <LoadableSelect
        autoload={value && Array.isArray(value) && value.length > 0 ? value : false}
        createable={createable}
        load={customLoad}
        raw
        multi
        onChange={onChangeInner}
        placeholder={placeholder}
        process={process}
        simpleValue={false}
        style={style}
        value={displayValue}
        disabled={disabled}
        virtualized
        // The `ignoreAccents` option is set to `false` to massively speed it up when we have 40,000+ tags (for org 1269)
        // https://www.botsplash.com/post/optimize-your-react-select-component-to-smoothly-render-10k-data
        ignoreAccents={false}
      />
    )
  }
)
export default TagSelector
